import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtPrice } from "./priceUtils";

const AUD = { agent: "Agent", reseller: "Reseller", sp: "Service Provider" };
const errMsg = (e, d) => formatApiErrorDetail(e.response?.data?.detail) || d;

/** Last month's billed unit price per partner vs their current list price. */
export function BillingCheckTab({ batches }) {
  const [d, setD] = useState(null);
  const [only, setOnly] = useState(false);
  const [bid, setBid] = useState("");
  useEffect(() => {
    setD(null);
    api.get("/price-list/billing-check", { params: { batch_id: bid } }).then(({ data }) => setD(data))
      .catch((e) => toast.error(errMsg(e, "Could not load the billing check")));
  }, [bid]);
  if (!d) return <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" />;
  const rows = only ? d.rows.filter((r) => r.flag) : d.rows;
  return (
    <div className="space-y-2" data-testid="price-billing-check">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <select value={bid} onChange={(e) => setBid(e.target.value)} className="h-8 rounded-md border border-slate-200 px-2" data-testid="billing-check-batch-select">
          <option value="">All linked items</option>
          {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{`Only items in: ${b.name} · ${b.effective_date}`}</option>)}
        </select>
        <span>Billing month <b>{d.period}</b></span>
        <span data-testid="billing-check-flagged"><b className="text-red-600">{d.flagged}</b> line(s) billed off the list price</span>
        {d.unlinked_items > 0 && <span className="text-amber-700" data-testid="billing-check-unlinked">{d.unlinked_items} live item(s) not linked yet</span>}
        <label className="ml-auto flex items-center gap-1.5"><input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} data-testid="billing-check-only-flagged" /> Only flagged</label>
      </div>
      <BillingTable rows={rows} empty={d.rows.length ? "No flagged lines." : bid ? "No billed usage for this batch's linked items." : "Nothing to show — link items to billing products first."} />
    </div>
  );
}

function BillingTable({ rows, empty }) {
  if (!rows.length) return <p className="py-6 text-center text-xs text-slate-400" data-testid="billing-check-empty">{empty}</p>;
  return (
    <div className="max-h-[55vh] overflow-auto rounded-md border border-slate-200">
      <table className="w-full text-xs" data-testid="billing-check-table">
        <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr><th className="px-2 py-2 text-left">Partner</th><th className="px-2 text-left">Item</th><th className="px-2 text-right">Qty</th>
            <th className="px-2 text-right">Billed / unit</th><th className="px-2 text-right">List price</th><th className="px-2"></th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.org_id}-${r.item_id}`} className={`border-t border-slate-100 ${r.flag ? "bg-red-50/40" : ""}`} data-testid={`billing-check-row-${r.org_id}-${r.item_id}`}>
              <td className="px-2 py-1.5">{r.partner}<div className="text-[10px] text-slate-400">{AUD[r.audience]}</div></td>
              <td className="px-2">{r.item}</td>
              <td className="px-2 text-right tabular-nums">{r.qty}</td>
              <td className="px-2 text-right tabular-nums">{fmtPrice(r.unit_billed)}</td>
              <td className="px-2 text-right tabular-nums">{r.list_price == null ? "On request" : fmtPrice(r.list_price)}</td>
              <td className="px-2 text-right">{r.flag && <span className="inline-flex items-center gap-1 font-semibold text-red-600"><AlertTriangle className="h-3 w-3" />{r.flag === "below" ? "Below list" : "Above list"}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Estimated monthly $ change per partner for a draft or scheduled batch. */
export function ImpactTab({ batches }) {
  const [bid, setBid] = useState(batches[0]?.batch_id || "");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!bid) return;
    setD(null);
    api.get(`/price-list/batches/${bid}/impact`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e, "Could not estimate impact")));
  }, [bid]);
  const csv = async () => {
    try {
      const { data } = await api.get(`/price-list/batches/${bid}/impact`, { params: { format: "csv" }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `impact-${bid}.csv`; a.click();
    } catch (e) { toast.error("Could not export"); }
  };
  if (!batches.length) return <p className="py-6 text-center text-xs text-slate-400" data-testid="impact-no-batches">No draft or scheduled change batches.</p>;
  return (
    <div className="space-y-2" data-testid="price-impact">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select value={bid} onChange={(e) => setBid(e.target.value)} className="h-8 rounded-md border border-slate-200 px-2" data-testid="impact-batch-select">
          {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{`${b.name} · ${b.effective_date} · ${b.status}`}</option>)}
        </select>
        {d && <span data-testid="impact-total">Based on {d.period} billing · total <b className={d.total_monthly_change < 0 ? "text-red-600" : "text-emerald-700"}>{fmtPrice(d.total_monthly_change)}</b>/month</span>}
        <Button size="sm" variant="outline" className="ml-auto h-8" onClick={csv} disabled={!d} data-testid="impact-export-csv"><Download className="h-3.5 w-3.5" /> CSV</Button>
      </div>
      {d?.unlinked_items?.length > 0 && <p className="text-[11px] text-amber-700" data-testid="impact-unlinked">Not linked to billing, so not counted: {d.unlinked_items.join(", ")}</p>}
      {!d ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" /> : <ImpactTable rows={d.partners} />}
    </div>
  );
}

const SORTS = { partner: (r) => r.partner.toLowerCase(), qty: (r) => r.qty, change: (r) => r.monthly_change };

function ImpactTable({ rows: raw }) {
  const [sort, setSort] = usePersistedState("price-list-check.sort", { key: "change", dir: -1 });
  const rows = [...raw].sort((a, b) => {
    const x = SORTS[sort.key](a), y = SORTS[sort.key](b);
    return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
  });
  const th = (key, label, cls) => (
    <th className={`cursor-pointer select-none px-2 py-2 hover:text-slate-800 ${cls}`} data-testid={`impact-sort-${key}`}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : key === "partner" ? 1 : -1 }))}>
      {label}{sort.key === key ? (sort.dir < 0 ? " ↓" : " ↑") : ""}</th>
  );
  if (!rows.length) return <p className="py-6 text-center text-xs text-slate-400" data-testid="impact-empty">No billed usage found for the changed items.</p>;
  return (
    <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
      <table className="w-full text-xs" data-testid="impact-table">
        <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{th("partner", "Partner", "text-left")}{th("qty", "Billed qty", "text-right")}{th("change", "Est. monthly change", "text-right")}<th className="px-2 text-left">Items</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.org_id} className="border-t border-slate-100" data-testid={`impact-row-${r.org_id}`}>
              <td className="px-2 py-1.5">{r.partner}<div className="text-[10px] text-slate-400">{AUD[r.audience]}</div></td>
              <td className="px-2 text-right tabular-nums">{r.qty}</td>
              <td className={`px-2 text-right font-semibold tabular-nums ${r.monthly_change < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtPrice(r.monthly_change)}</td>
              <td className="px-2 text-[11px] text-slate-500">{r.items.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
